// components/thread-history-item.js
// ═══════════════════════════════════════════════════════════
// Single thread history item. Display only.
// Owns: title display, hover state, ··· menu, rename input
// Never owns: delete logic, rename persistence (calls props)
// ═══════════════════════════════════════════════════════════
import { useState } from 'react';

export default function ThreadHistoryItem({ t, i, total, currentSessionId, onLoad, onDelete, onRename, onRegenerateTitle }) {
  const [showMenu, setShowMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  const rawTitle = t.title || t.what_shifted || `Session ${total - i}`;
  const title = rawTitle.replace(/^the user (discusses|explores|asks|talks|shares|wants|is|was|has|had)\s+/i, '').replace(/^./, c => c.toUpperCase());

  const isActive = t.id === currentSessionId;

  function handleRename() {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== title) onRename?.(t.id, trimmed);
    setEditing(false);
    setShowMenu(false);
  }

  if (editing) {
    return (
      <div style={{ padding: '4px 10px', borderRadius: 6, background: '#1e1e2a', marginBottom: 2 }}>
        <input
          autoFocus
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') handleRename();
            if (e.key === 'Escape') { setEditing(false); setShowMenu(false); }
          }}
          onBlur={handleRename}
          style={{
            background: '#252525', border: '1px solid #444', borderRadius: 4,
            color: '#d4d4d4', fontSize: 13, fontFamily: 'Georgia, serif',
            padding: '4px 8px', width: '100%', boxSizing: 'border-box', outline: 'none',
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{ position: 'relative', marginBottom: 2 }}
      onMouseLeave={() => setShowMenu(false)}
    >
      <div
        style={{
          padding: '7px 10px', borderRadius: 6,
          background: isActive ? '#272736' : 'none',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#1e1e2a'; }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'none'; }}
        onClick={() => { if (!showMenu) onLoad?.(t.id, t.is_incognito); }}
      >
        {t.is_incognito && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginRight: 5 }}>
            <path d="M12 3a7 7 0 0 1 7 7v7l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5v-7a7 7 0 0 1 7-7z"
              stroke="#888888" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="9" cy="11" r="1" fill="#888888"/>
            <circle cx="15" cy="11" r="1" fill="#888888"/>
          </svg>
        )}
        <span style={{
          color: isActive ? '#d4d4d4' : '#888',
          fontSize: 13, fontFamily: 'Georgia, serif',
          overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', flex: 1,
        }}>
          {title}
        </span>
        <button
          onClick={e => { e.stopPropagation(); setShowMenu(v => !v); }}
          style={{
            background: showMenu ? '#252525' : 'none',
            border: showMenu ? '1px solid #333' : '1px solid transparent',
            borderRadius: 4, padding: '2px 6px', cursor: 'pointer',
            color: '#666', fontSize: 12, lineHeight: 1,
            opacity: showMenu ? 1 : 0, flexShrink: 0, marginLeft: 8,
            transition: 'opacity 0.15s',
          }}
          className="thread-menu-btn"
        >
          ···
        </button>
      </div>

      {showMenu && (
        <>
          <div
            onClick={() => setShowMenu(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 49 }}
          />
          <div style={{
            position: 'absolute', right: 0, top: 32,
            background: '#1c1c1c', border: '1px solid #2a2a2a',
            borderRadius: 8, overflow: 'hidden', zIndex: 50,
            width: 130, boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          }}>
            <button
              onClick={() => { setEditValue(title); setEditing(true); setShowMenu(false); }}
              style={{
                width: '100%', background: 'none', border: 'none',
                padding: '8px 12px', display: 'flex', alignItems: 'center',
                gap: 8, cursor: 'pointer', textAlign: 'left',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#252525'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path d="M2 11.5V14h2.5l7.4-7.4-2.5-2.5L2 11.5zM13.7 4.3a.7.7 0 000-1L12 1.6a.7.7 0 00-1 0L9.8 2.8l2.5 2.5 1.4-1z" fill="#888"/>
              </svg>
              <span style={{ color: '#888', fontSize: 13, fontFamily: 'Georgia, serif' }}>Rename</span>
            </button>
            {onRegenerateTitle && (
              <>
                <div style={{ height: 1, background: '#2a2a2a' }} />
                <button
                  disabled={regenerating}
                  onClick={async () => {
                    setRegenerating(true);
                    try { await onRegenerateTitle(t.id); } catch { /* parent surfaces errors */ }
                    setRegenerating(false);
                    setShowMenu(false);
                  }}
                  style={{
                    width: '100%', background: 'none', border: 'none',
                    padding: '8px 12px', display: 'flex', alignItems: 'center',
                    gap: 8, cursor: regenerating ? 'wait' : 'pointer', textAlign: 'left',
                    opacity: regenerating ? 0.5 : 1,
                  }}
                  onMouseEnter={e => { if (!regenerating) e.currentTarget.style.background = '#252525'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <path d="M14 8a6 6 0 11-1.76-4.24M14 2v3.5h-3.5" stroke="#7a9e82" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span style={{ color: '#7a9e82', fontSize: 13, fontFamily: 'Georgia, serif' }}>
                    {regenerating ? 'Regenerating...' : 'Regenerate'}
                  </span>
                </button>
              </>
            )}
            <div style={{ height: 1, background: '#2a2a2a' }} />
            <button
              onClick={() => { setShowMenu(false); onDelete?.(t.id); }}
              style={{
                width: '100%', background: 'none', border: 'none',
                padding: '8px 12px', display: 'flex', alignItems: 'center',
                gap: 8, cursor: 'pointer', textAlign: 'left',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#251a1a'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path d="M3 4h10M5 4V2h6v2M6 7v5M10 7v5M4 4l1 9h6l1-9" stroke="#c46050" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={{ color: '#c46050', fontSize: 13, fontFamily: 'Georgia, serif' }}>Delete</span>
            </button>
          </div>
        </>
      )}

      <style jsx>{`
        div:hover .thread-menu-btn { opacity: 1 !important; }
        .thread-menu-btn:hover { color: #7a9e82 !important; border-color: #7a9e82 !important; }
      `}</style>
    </div>
  );
}
